import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import styles from './Auth.module.css';

const SimpleRegisterForm = ({ onSuccess, onLoginClick }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { register, loading, sendVerificationEmail } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    return '';
  };

  // Real-time validation
  useEffect(() => {
    if (touched.email || email) {
      const error = validateEmail(email);
      setEmailError(error);
    }
  }, [email, touched.email]);

  useEffect(() => {
    if (touched.password || password) {
      const error = validatePassword(password);
      setPasswordError(error);
    }
  }, [password, touched.password]);

  // Check if form is valid
  const isFormValid = () => {
    return email && 
           password && 
           password.length >= 6 &&
           !validateEmail(email) && 
           !validatePassword(password);
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched to show errors
    setTouched({ email: true, password: true });
    
    // Final validation
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    
    if (emailValidation || passwordValidation) {
      setEmailError(emailValidation);
      setPasswordError(passwordValidation);
      return;
    }
    
    setEmailError('');
    setPasswordError('');

    try {
      const result = await register({
        email: email,
        password: password
      });
      
      if (!result) {
        setEmailError('Registration service error. Please try again.');
        return;
      }

      if (result.success) {
        const verificationResult = await sendVerificationEmail(email);
        if (verificationResult.success) {
          setShowSuccess(true);
          if (onSuccess) onSuccess();
        } else {
          setEmailError('Registration successful, but verification email failed. Please contact support.');
        }
        return; 
      }

      // Handle different error formats
      let errorMessage = 'Registration failed. Please try again.';
      
      // Firebase error object format
      if (result.error && typeof result.error === 'object') {
        switch (result.error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered. Try signing in instead.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Email/password accounts are not enabled. Please contact support.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection.';
            break;
          default:
            errorMessage = result.error.message || errorMessage;
        }
      } 
      // String error format
      else if (result.error && typeof result.error === 'string') {
        const errorStr = result.error.toLowerCase();
        if (errorStr.includes('email-already-in-use') || 
            errorStr.includes('already in use') ||
            errorStr.includes('already exists')) {
          errorMessage = 'This email is already registered. Try signing in instead.';
        } else if (errorStr.includes('invalid-email') || errorStr.includes('invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        } else if (errorStr.includes('weak-password') || errorStr.includes('weak password')) {
          errorMessage = 'Password is too weak. Please use at least 6 characters.';
        } else {
          errorMessage = result.error;
        }
      }
      
      setEmailError(errorMessage);

    } catch (err) {
      console.error("Registration error:", err);
      
      let errorMessage = 'Registration error. Please try again.';
      
      // Handle Firebase Auth errors from exception
      if (err.code) {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered. Try signing in instead.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection.';
            break;
          default:
            if (err.message) errorMessage = err.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setEmailError(errorMessage);
    }
  };

  if (showSuccess) {
    return (
      <div className={styles.formSection}>
        <h2 className={styles.successHeader}>🎉 Check Your Email!</h2>
        <div className={styles.successMessage}>
          <p><strong>We've sent a verification link to:</strong></p>
          <p className={styles.userEmail}>{email}</p>
          <p>Please click the link in the email to verify your account and start trading.</p>
          <p className={styles.note}>Can't find it? Check your spam folder.</p>
          {onLoginClick && (
            <button 
              onClick={onLoginClick}
              className={styles.linkButton}
              style={{ marginTop: '1.5rem' }}
            >
              Return to Sign In
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formSection}>
      <h2>Join ONSERV Trader</h2>
      <p className={styles.subtext}>Start your trading journey with AI-powered insights.</p>
      
      <form onSubmit={handleSubmit} className={styles.authForm} noValidate>
        <div className={styles.stackingGroup}>
          <div className={styles.labelRow}>
            <label htmlFor="email">Email</label>
            {emailError && (
              <span className={styles.errorBadge}>
                <span className={styles.xSymbol}>✕</span> {emailError}
              </span>
            )}
          </div>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur('email')}
            required
            placeholder="your.email@example.com"
            className={`${styles.authInput} ${emailError ? styles.errorInput : ''}`}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "email-error" : undefined}
          />
        </div>
        
        <div className={styles.stackingGroup}>
          <div className={styles.labelRow}>
            <label htmlFor="password">Password</label>
            {passwordError && (
              <span className={styles.errorBadge}>
                <span className={styles.xSymbol}>✕</span> {passwordError}
              </span>
            )}
          </div>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => handleBlur('password')}
            required
            placeholder="Min. 6 characters"
            minLength="6"
            className={`${styles.authInput} ${passwordError ? styles.errorInput : ''}`}
            aria-invalid={!!passwordError}
            aria-describedby={passwordError ? "password-error" : undefined}
          />
          {password && !passwordError && (
            <div className={styles.passwordHint}>
              {password.length < 6 ? 'Too short' : 'Good'}
            </div>
          )}
        </div>

        <button 
          type="submit" 
          className={styles.submitButton} 
          disabled={loading || !isFormValid()}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className={styles.spinner}></span>
              Creating Account...
            </>
          ) : 'Start Free Trial'}
        </button>
      </form>

      {onLoginClick && (
        <p className={styles.switchForm}>
          Already have an account?{' '}
          <span 
            className={styles.linkButton} 
            onClick={() => onLoginClick()}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onLoginClick()}
          >
            Sign in here
          </span>
        </p>
      )}
    </div>
  );
};

SimpleRegisterForm.propTypes = {
  onSuccess: PropTypes.func,
  onLoginClick: PropTypes.func,
};

export default SimpleRegisterForm;